import React from 'react';
import styled, { css } from 'styled-components';
import TinaResponsiveAsset from 'components/TinaResponsiveAsset';
import StyledRichText from 'components/StyledRichText';
import type { ProjectHero as T } from './types';
import { blockPadV, blockPadH, blockW } from 'theme/mixins/padding';
import FormattedText from 'components/FormattedText';
import { Maybe } from '.tina/__generated__/types';

interface IStyledAsset {
  fullImageHeight: Maybe<boolean> | undefined;
}

const Wrapper = styled.div`
  ${blockPadV({ pt: 'no', pb: 'no' })}
  ${({ theme }) => theme.mq.md`
    ${blockPadH()};
  `};
  background: ${({ theme }) => theme.themeColors.bg};
`;

const Container = styled.div`
  ${blockW()}
`;

const Content = styled.div`
  ${() => blockPadH()}
  padding-top: 1.4375rem;
  ${({ theme }) => theme.mq.md`
    padding: 0;
  `}
`;

const StyledAsset = styled(TinaResponsiveAsset)<IStyledAsset>`
  object-fit: cover;
  width: 100%;
  margin: 3px;
  ${({ theme: { mq, gfx }, fullImageHeight }) => css`
    max-height: ${fullImageHeight ? 'auto' : '70vh'};

    ${mq.md`
    ${gfx.mixins.mv(5, { md: 3 })}
    max-height: ${fullImageHeight ? 'auto' : '665px'};

    margin: 0;
  `}
  `}
`;

const ProjectName = styled.h1`
  color: ${({ theme }) => theme.themeColorOpacities.fg50};
  ${({ theme }) => theme.gfx.mixins.mb(0)}
  line-height: 2.8125rem;
`;

const ProjectLead = styled.h1`
  ${({ theme }) => theme.gfx.mixins.mb(6, { md: 5 })}
  color: ${({ theme }) => theme.themeColors.fg};
  line-height: 2.8125rem;
  ${({ theme }) => theme.mq.lg`
    max-width: 70%;
  `}
`;

const ProjectDetailItems = styled.div`
  display: flex;
  p {
    margin-bottom: 0;
  }
`;

const TopGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 3rem;
  ${({ theme }) => theme.mq.md`
    grid-template-columns: 1fr 2fr;
  `}
  ${({ theme }) => theme.mq.lg`
    grid-template-columns: 1fr 2fr 0.5fr;
  `}
  ${({ theme }) => theme.gfx.mixins.mb(6, { lg: 0 })}
`;

const ProjectDetail = styled.div``;

const DetailLabel = styled(FormattedText)`
  color: ${({ theme }) => theme.themeColorOpacities.fg50};
  ${({ theme }) => theme.gfx.mixins.mb(4, { md: 2 })};
`;

const DetailParagraph = styled(FormattedText)`
  color: ${({ theme }) => theme.themeColors.fg};
  ${({ theme }) => theme.gfx.mixins.mb(0)}
`;

const ProjectHero: React.FC<T> = ({
  respImage,
  projectName,
  projectLead,
  projectScopeItems,
  projectAbout,
  projectAboutRichText,
  fullImageHeight
}) => {
  return (
    <Wrapper>
      <Container>
        {respImage && <StyledAsset asset={respImage} fullImageHeight={fullImageHeight} />}
        <Content>
          {projectName && <ProjectName>{projectName}</ProjectName>}
          {projectLead && <ProjectLead>{projectLead}</ProjectLead>}
          <ProjectDetailItems>
            <TopGroup>
              <ProjectDetail>
                <DetailLabel pFormat={'monoRegular16'}>Scope</DetailLabel>
                {projectScopeItems?.body && projectScopeItems?.pFormat && (
                  <StyledRichText
                    pFormat={projectScopeItems.pFormat}
                    content={projectScopeItems.body}
                  />
                )}
              </ProjectDetail>
              <ProjectDetail>
                <DetailLabel pFormat={'monoRegular16'}>About</DetailLabel>
                {projectAbout && (
                  <DetailParagraph pFormat={'sansRegular24'}>{projectAbout}</DetailParagraph>
                )}
                {projectAboutRichText && (
                  <StyledRichText
                    pFormat={projectAboutRichText.pFormat}
                    content={projectAboutRichText.body}
                  />
                )}
              </ProjectDetail>
            </TopGroup>
          </ProjectDetailItems>
        </Content>
      </Container>
    </Wrapper>
  );
};

export default ProjectHero;
